<template>
    <teleport to="body">
        <transition name="cmp-fade">
            <div v-if="open" class="cmp-backdrop" @click="close"></div>
        </transition>
        <transition name="cmp-slide">
            <aside v-if="open" class="column-manager-panel">
                <header class="cmp-header">
                    <span class="cmp-title">{{ $t('Manage columns') }}</span>
                    <a class="cmp-close" :title="$t('Close')" @click="close">
                        <b-icon icon="close" size="is-small" />
                    </a>
                </header>

                <p class="cmp-hint">{{ $t('Show, hide and reorder columns for your account.') }}</p>

                <div class="cmp-body">
                    <draggable
                        :list="columns"
                        item-key="field"
                        handle=".cmp-drag"
                        ghost-class="cmp-ghost"
                        @end="onReorder"
                    >
                        <template #item="{ element }">
                            <div class="cmp-row">
                                <span class="cmp-drag" :title="$t('Drag to reorder')">
                                    <b-icon icon="drag" size="is-small" />
                                </span>
                                <b-checkbox
                                    class="cmp-check"
                                    v-model="element.visible"
                                    :disabled="element.locked"
                                    @update:model-value="apply"
                                >
                                    {{ element.label }}
                                </b-checkbox>
                                <b-icon
                                    v-if="element.locked"
                                    icon="lock"
                                    size="is-small"
                                    class="cmp-lock"
                                    :title="$t('This column cannot be hidden')"
                                />
                            </div>
                        </template>
                    </draggable>
                </div>

                <!-- Per-list settings that are not columns. The layout is the user's
                     own answer for this list alone; left on "the organization's
                     choice" it keeps following Settings ▸ Lists. -->
                <div class="cmp-settings">
                    <b-field :label="$t('Display mode')" label-position="on-border">
                        <b-select v-model="layoutMode" expanded @update:model-value="saveLayoutMode">
                            <option value="">{{ $t("Organization's choice") }}</option>
                            <option value="adaptive">{{ $t('Full-width table') }}</option>
                            <option value="narrow">{{ $t('Narrow list') }}</option>
                            <option value="full">{{ $t('Full width at all times') }}</option>
                        </b-select>
                    </b-field>
                    <p class="cmp-hint cmp-hint--inline">{{ layoutHint }}</p>

                    <!-- How a record opened from this list opens. It only ever
                         applies where the user may edit the record: on one they
                         may only read, the fiche opens read-only whatever is
                         chosen here. -->
                    <b-field :label="$t('Opening a record')" label-position="on-border" class="mt-4">
                        <b-select v-model="openMode" expanded @update:model-value="saveOpenMode">
                            <option value="">{{ $t("Organization's choice") }}</option>
                            <option value="read">{{ $t('In read mode') }}</option>
                            <option value="edit">{{ $t('In edit mode') }}</option>
                        </b-select>
                    </b-field>
                    <p class="cmp-hint cmp-hint--inline">{{ openModeHint }}</p>
                </div>

                <footer class="cmp-footer">
                    <a class="cmp-reset" @click="reset">
                        <b-icon icon="restore" size="is-small" />
                        <span>{{ $t('Reset columns') }}</span>
                    </a>
                    <a v-if="resource" class="cmp-reset" @click="resetWidths">
                        <b-icon icon="arrow-collapse-horizontal" size="is-small" />
                        <span>{{ $t('Reset column widths') }}</span>
                    </a>
                </footer>
            </aside>
        </transition>
    </teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useListConfigStore } from '@/stores/listConfig';
import { useGridPreferencesStore } from '@/stores/gridPreferences';
import { useOrgStore } from '@/stores/org';
import { mergeUserColumns } from '@/composables/useListColumns';

const props = defineProps({
    // ListColumnCatalog object type (e.g. 'contacts', 'tasks'); also the grid key
    // under which the user's column override is stored.
    objectType: {
        type: String,
        required: true
    },
    // Grid key under which the resized column widths are stored (the grid's
    // resource slug). Enables the "reset column widths" action in the panel.
    resource: {
        type: String,
        default: ''
    },
    open: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:open']);

const listConfig = useListConfigStore();
const gridPreferences = useGridPreferencesStore();
const orgStore = useOrgStore();

// Local, editable copy of the columns (org config merged with the user override).
const columns = ref([]);

// This user's layout for this list; '' follows the organization's choice.
const layoutMode = ref('');

// What the chosen layout does, in one line — the three names do not say what
// happens when a record is opened, which is the whole difference between two of
// them. On "the organization's choice" it reads the org's current answer, so the
// line always describes what this list will actually do.
const LAYOUT_HINTS = {
    adaptive: 'The list fills the screen; opening a record brings the narrow list back beside it.',
    narrow: 'Every list keeps the classic narrow layout with a side preview.',
    full: 'The list fills the screen, and an opened record takes it whole: the list steps aside.'
};

const layoutHint = computed(() => {
    const effective = layoutMode.value || orgStore.listLayoutMode;
    return i18n.t(LAYOUT_HINTS[effective] ?? LAYOUT_HINTS.adaptive);
});

// This user's opening mode for this list; '' follows the organization's.
const openMode = ref('');

// What the chosen mode does, and the one condition on it — a record the user may
// not edit opens read-only whichever mode is picked. On "the organization's
// choice" the line names the org's current answer, so it describes what this list
// will actually do.
const OPEN_MODE_HINTS = {
    read: 'Records open read-only; the pencil puts them in edit mode.',
    edit: 'Records open ready to edit, where you may edit them.'
};

const openModeHint = computed(() => {
    if (openMode.value) {
        return i18n.t(OPEN_MODE_HINTS[openMode.value]);
    }
    return i18n.t(orgStore.objectEditModeEnabled ? OPEN_MODE_HINTS.edit : OPEN_MODE_HINTS.read);
});

function build() {
    const config = listConfig.getConfig(props.objectType);
    if (!config || !config.length) {
        columns.value = [];
        return;
    }
    const merged = mergeUserColumns(config, gridPreferences.getColumnConfig(props.objectType));
    columns.value = merged.map(column => ({
        field: column.field,
        label: column.label,
        visible: !!column.visible,
        locked: !!column.locked
    }));
}

// Rebuild from the stores each time the panel opens (and load the config if a
// grid that hasn't yet is opened straight into the manager).
watch(
    () => props.open,
    async open => {
        if (!open) {
            return;
        }
        layoutMode.value = gridPreferences.getLayoutMode(props.objectType);
        openMode.value = gridPreferences.getOpenMode(props.objectType);
        if (!listConfig.getConfig(props.objectType)) {
            await listConfig.loadConfig(props.objectType);
        }
        build();
    }
);

// Live-apply: the list reads the store, so it re-lays itself out behind the panel.
function saveLayoutMode(mode) {
    gridPreferences.saveLayoutMode(props.objectType, mode);
}

// Read on the next record opened from this list — nothing to re-render here.
function saveOpenMode(mode) {
    gridPreferences.saveOpenMode(props.objectType, mode);
}

// sortablejs-vue3 does not mutate the bound list — reorder it manually.
function onReorder(evt) {
    if (evt.oldIndex === evt.newIndex) {
        return;
    }
    const [moved] = columns.value.splice(evt.oldIndex, 1);
    columns.value.splice(evt.newIndex, 0, moved);
    apply();
}

// Live-apply: persist the override; the grid re-renders reactively via useListColumns.
function apply() {
    gridPreferences.saveColumnConfig(props.objectType, columns.value);
}

function reset() {
    gridPreferences.resetColumns(props.objectType);
    build();
}

// Reset resized widths back to the defaults; the resize directive watches the
// store and re-applies them to the grid.
function resetWidths() {
    if (props.resource) {
        gridPreferences.resetColumnWidths(props.resource);
    }
}

function close() {
    emit('update:open', false);
}
</script>

<style scoped>
.cmp-backdrop {
    position: fixed;
    inset: 0;
    background: var(--elx-scrim-weak);
    z-index: 90;
}
.column-manager-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 320px;
    max-width: 90vw;
    z-index: 91;
    display: flex;
    flex-direction: column;
    background: var(--bulma-scheme-main);
    border-left: 1px solid var(--bulma-border-weak);
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.12);
}
.cmp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--bulma-border-weak);
}
.cmp-title {
    font-weight: 600;
}
.cmp-close {
    color: var(--bulma-text-weak);
    line-height: 1;
}
.cmp-close:hover {
    color: var(--bulma-text);
}
.cmp-hint {
    padding: 0.6rem 1rem 0;
    font-size: 0.8rem;
    color: var(--bulma-text-weak);
}
.cmp-settings {
    padding: 1rem 1rem 0.25rem;
    border-top: 1px solid var(--bulma-border-weak);
}
.cmp-hint--inline {
    padding: 0.35rem 0 0;
}
.cmp-body {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
}
.cmp-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.5rem;
    border-radius: var(--bulma-radius);
}
.cmp-row:hover {
    background: var(--bulma-background);
}
.cmp-drag {
    cursor: grab;
    color: var(--bulma-text-weak);
    display: inline-flex;
}
.cmp-check {
    flex: 1;
    min-width: 0;
}
.cmp-lock {
    color: var(--bulma-text-weak);
}
.cmp-ghost {
    opacity: 0.5;
}
.cmp-footer {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--bulma-border-weak);
}
.cmp-reset {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: var(--bulma-text-weak);
    font-size: 0.85rem;
}
.cmp-reset:hover {
    color: var(--bulma-danger);
}

/* Slide + fade transitions */
.cmp-slide-enter-active,
.cmp-slide-leave-active {
    transition: transform 0.22s ease;
}
.cmp-slide-enter-from,
.cmp-slide-leave-to {
    transform: translateX(100%);
}
.cmp-fade-enter-active,
.cmp-fade-leave-active {
    transition: opacity 0.22s ease;
}
.cmp-fade-enter-from,
.cmp-fade-leave-to {
    opacity: 0;
}
</style>
